import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, Popover, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { getCurrentUser } from '../../services/auth';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const SLOT_H = 16; // фіксована висота 15-хв слота (px) — компактно й без розтягування рядків
const DAY_SLOT_H = 22; // висота слота в діалозі перегляду дня (px)

// Ефективна ціна запису: (індивідуальна b.price або сума послуг) мінус знижка промокоду.
const effPrice = (b) => {
  const base = b.price != null
    ? Number(b.price)
    : (Array.isArray(b.services)
        ? b.services.reduce((acc, s) => acc + Number(s.price || 0), 0)
        : 0);
  return Math.max(0, base - Number(b.discountAmount || 0));
};

export default function DashboardCalendarView() {
  const { t, i18n } = useTranslation();
  const { workspaceRole, activeWorkspaceId } = useAuth();
  // Менеджер салону (owner/admin) бачить весь календар салону, не лише свій
  const isManager = workspaceRole === 'owner' || workspaceRole === 'admin';
  const DAYS = [
    t('dashboard.mon'), t('dashboard.tue'), t('dashboard.wed'),
    t('dashboard.thu'), t('dashboard.fri'), t('dashboard.sat'), t('dashboard.sun'),
  ];
  const [bookings, setBookings] = useState([]);
  const [members, setMembers] = useState([]); // працівники салону (для фільтра)
  const [filterMasterId, setFilterMasterId] = useState(''); // '' = весь салон
  const [users, setUsers] = useState([]);
  const [timeOff, setTimeOff] = useState([]); // закриті блоки/вихідні
  const [workHours, setWorkHours] = useState([]); // тижневі робочі години майстра
  const [showAllHours, setShowAllHours] = useState(false); // показати весь день (0–24) для ручного запису поза графіком
  // Переглянутий тиждень зберігаємо в sessionStorage — щоб при поверненні з
  // деталей запису (кнопка «Назад») лишатись на тому ж тижні/місяці, а не
  // стрибати на поточний. «Сьогодні» та явний клік «Панель» скидають на 0.
  const [weekOffset, setWeekOffset] = useState(() => {
    const v = Number(sessionStorage.getItem('dashWeekOffset'));
    return Number.isFinite(v) ? v : 0;
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [slotMenu, setSlotMenu] = useState(null); // { anchor, date, time }
  const [blockMenu, setBlockMenu] = useState(null); // { anchor, block }
  const [closeDlg, setCloseDlg] = useState(null); // { date, start, end, reason }
  const [openDlg, setOpenDlg] = useState(null); // «відкрити години»: { date, start, end, existingId }
  const [editBlockDlg, setEditBlockDlg] = useState(null); // редагування закритого блоку
  const [dayOverrides, setDayOverrides] = useState([]); // per-date «відкриті» години
  const [warn, setWarn] = useState(''); // попап-попередження (конфлікт із бронню тощо)
  const [dayView, setDayView] = useState(null); // дата (YYYY-MM-DD) для перегляду одного дня
  const navigate = useNavigate();
  const location = useLocation();

  // Персист переглянутого тижня
  useEffect(() => {
    sessionStorage.setItem('dashWeekOffset', String(weekOffset));
  }, [weekOffset]);

  // Явний перехід у «Панель» (nav) — завжди поточний тиждень
  useEffect(() => {
    if (location.state?.resetWeek) {
      setWeekOffset(0);
      sessionStorage.setItem('dashWeekOffset', '0');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const today = dayjs();
  const startOfWeek = today.startOf('isoWeek').add(weekOffset, 'week');
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));

  const reload = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      if (!current || !current.id) {
        navigate('/login');
        return;
      }
      const [bookingList, userList, sched] = await Promise.all([
        api.getBookings(),
        api.getUsers(),
        api.getSchedule().catch(() => ({ timeOff: [] })),
      ]);
      const mine = bookingList
        // Менеджер бачить усі записи салону (бек уже скоупить за салоном) з
        // опційним фільтром по майстру; майстер-виконавець — лише свої.
        .filter(b => {
          if (!isManager) return b.masterId === current.id;
          return filterMasterId ? b.masterId === Number(filterMasterId) : true;
        })
        .map(b => {
          const u = userList.find(u => u.id === b.userId);
          // Слід у календарі = зарезервований час (block_duration); беремо
          // авторитетний durationMinutes із бекенду, з фолбеком на суму послуг.
          const totalDuration = b.durationMinutes ?? (Array.isArray(b.services)
            ? b.services.reduce((acc, s) => acc + (s.blockDuration ?? s.duration ?? 0), 0)
            : 0);
          const serviceNames = Array.isArray(b.services)
            ? b.services.map(s => pickServiceName(s, i18n.language)).join(', ')
            : '';
          return {
            ...b,
            // Імʼя клієнта приходить із самої броні (join у бекенді); userList — запасний варіант
            userName: b.clientName || u?.name || t('dashboard.clientFallback'),
            servicesLabel: serviceNames,
            masterName: b.masterName,
            duration: totalDuration,
          };
        });
      setBookings(mine);
      setUsers(userList);
      setTimeOff(sched.timeOff || []);
      setDayOverrides(sched.dayOverrides || []);
      setWorkHours(sched.workHours || []);
    } catch (e) {
      setBookings([]);
      setUsers([]);
      setTimeOff([]);
      setWorkHours([]);
    }
  }, [navigate, i18n.language, isManager, filterMasterId]);

  useEffect(() => { reload(); }, [reload]);

  // Працівники салону — для фільтра календаря (owner/admin)
  useEffect(() => {
    if (!isManager || !activeWorkspaceId) return;
    api.getWorkspaceMembers(activeWorkspaceId)
      .then((r) => setMembers(r.members || []))
      .catch(() => {});
  }, [isManager, activeWorkspaceId]);

  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

  // Видимий діапазон годин = робочі години майстра (найраніший старт → найпізніший кінець
  // серед робочих днів тижня), розширений, щоб покрити будь-які закриті блоки/броні.
  const workingRows = workHours.filter(w => w.isWorking && w.startTime && w.endTime);
  let rangeStart = workingRows.length ? Math.min(...workingRows.map(w => toMin(w.startTime))) : 9 * 60;
  let rangeEnd = workingRows.length ? Math.max(...workingRows.map(w => toMin(w.endTime))) : 18 * 60;
  // Часткові закриті блоки (не цілі вихідні) мають бути видимі, навіть якщо поза робочими годинами
  for (const o of timeOff) {
    if (o.fullDay || !o.startTime || !o.endTime) continue;
    rangeStart = Math.min(rangeStart, toMin(o.startTime));
    rangeEnd = Math.max(rangeEnd, toMin(o.endTime));
  }
  // «Весь день» (0–24) дає майстру створити запис у будь-яку годину поза графіком
  const startHour = showAllHours ? 0 : Math.floor(rangeStart / 60);
  const endHour = showAllHours ? 24 : Math.max(startHour + 1, Math.ceil(rangeEnd / 60));
  const slotCount = (endHour - startHour) * 4; // по 15 хв

  // Робоче вікно конкретного дня тижня (0=нд … 6=сб, як у бекенді)
  const whByWeekday = new Map(workHours.map(w => [w.weekday, w]));
  const ovByDate = new Map(dayOverrides.map(o => [o.date, o]));
  // Слот поза робочими годинами саме цього дня (коротший день / тижневий вихідний)
  const isOutsideWork = (d, timeLabel) => {
    const slot = toMin(timeLabel);
    // «Відкрите» на дату вікно робить слот робочим навіть у вихідний день тижня
    const ov = ovByDate.get(d.format('YYYY-MM-DD'));
    if (ov && slot >= toMin(ov.startTime) && slot < toMin(ov.endTime)) return false;
    const w = whByWeekday.get(d.day());
    if (w && w.isWorking && w.startTime && w.endTime &&
        slot >= toMin(w.startTime) && slot < toMin(w.endTime)) return false;
    return true;
  };
  // Закритий інтервал (блок або цілий вихідний) на цей слот
  const getBlockAt = (dateStr, timeLabel) => {
    const slot = toMin(timeLabel);
    return timeOff.find(o =>
      o.date === dateStr &&
      (o.fullDay || (slot >= toMin(o.startTime) && slot < toMin(o.endTime)))
    );
  };

  // ---- CALENDAR LOGIC ----
  const getBookingAt = (dateStr, timeStr) => {
    // Повертає букінг, якщо цей слот входить у інтервал [start, end)
    const slotTime = dayjs(`${dateStr}T${timeStr}`);
    return bookings.find(b => {
      const start = dayjs(`${dayjs(b.date).format('YYYY-MM-DD')}T${b.time.slice(0,5)}`);
      const end = start.add(b.duration, 'minute');
      return (slotTime.isSame(start) || (slotTime.isAfter(start) && slotTime.isBefore(end)));
    });
  };

  const isStartOfBooking = (b, slotTime) => {
    const localDate = dayjs(b.date).format('YYYY-MM-DD');
    const start = dayjs(`${localDate}T${b.time.slice(0,5)}`);
    return slotTime.isSame(start);
  };

  const isEndOfBooking = (b, slotTime) => {
    const localDate = dayjs(b.date).format('YYYY-MM-DD');
    const start = dayjs(`${localDate}T${b.time.slice(0,5)}`);
    const end = start.add(b.duration, 'minute').subtract(15, 'minute');
    return slotTime.isSame(end);
  };

  const goToToday = () => { setWeekOffset(0); sessionStorage.setItem('dashWeekOffset', '0'); };
  const handleCalendarOpen = (event) => setAnchorEl(event.currentTarget);
  const handleCalendarClose = () => setAnchorEl(null);
  const handleDateChange = (value) => {
    const diff = value.startOf('isoWeek').diff(today.startOf('isoWeek'), 'week');
    setWeekOffset(diff);
    setAnchorEl(null);
  };

  const handleBookingClick = (id) => {
    navigate(`/master/booking/${id}`);
  };

  // Меню по кліку на вільний слот
  const openSlotMenu = (e, dateStr, timeLabel) =>
    setSlotMenu({ anchor: e.currentTarget, date: dateStr, time: timeLabel });
  const createFromSlot = () => {
    if (slotMenu) navigate(`/master/create?date=${slotMenu.date}&time=${slotMenu.time}`);
    setSlotMenu(null);
  };
  const openCloseDialog = () => {
    if (!slotMenu) return;
    const end = dayjs(`${slotMenu.date}T${slotMenu.time}`).add(60, 'minute').format('HH:mm');
    setCloseDlg({ date: slotMenu.date, start: slotMenu.time, end, reason: '' });
    setSlotMenu(null);
  };
  const submitClose = async () => {
    try {
      await api.addTimeOff({
        date: closeDlg.date,
        startTime: closeDlg.start,
        endTime: closeDlg.end,
        reason: closeDlg.reason || undefined,
      });
      setCloseDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.closeTimeError'));
    }
  };

  // Меню по кліку на закритий блок
  const openBlockMenu = (e, block) => setBlockMenu({ anchor: e.currentTarget, block });

  // «Відкрити робочі години» на конкретну дату — ДОДАЄ вікно поверх графіка.
  // Префіл від клікнутого слота + 2 год. Якщо на дату вже є вікно — редагуємо.
  const openOpenHoursDialog = () => {
    if (!slotMenu) return;
    const existing = dayOverrides.find(o => o.date === slotMenu.date);
    const defEnd = dayjs(`${slotMenu.date}T${slotMenu.time}`).add(2, 'hour').format('HH:mm');
    setOpenDlg({
      date: slotMenu.date,
      start: existing?.startTime || slotMenu.time,
      end: existing?.endTime || (defEnd === '00:00' ? '23:59' : defEnd),
      existingId: existing?.id || null,
    });
    setSlotMenu(null);
  };
  const submitOpenHours = async () => {
    try {
      await api.setDayOverride({ date: openDlg.date, startTime: openDlg.start, endTime: openDlg.end });
      setOpenDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openHoursError'));
    }
  };
  const removeOpenHours = async () => {
    if (!openDlg?.existingId) { setOpenDlg(null); return; }
    try {
      await api.deleteDayOverride(openDlg.existingId);
      setOpenDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openHoursError'));
    }
  };
  // Відкрити/редагувати години на дату з перегляду дня
  const openHoursForDay = (dateStr) => {
    const existing = dayOverrides.find(o => o.date === dateStr);
    const w = whByWeekday.get(dayjs(dateStr).day());
    setOpenDlg({
      date: dateStr,
      start: existing?.startTime || w?.startTime || '09:00',
      end: existing?.endTime || w?.endTime || '18:00',
      existingId: existing?.id || null,
    });
  };
  // Редагувати закритий блок (лише частковий — не цілий вихідний)
  const openEditBlockDialog = () => {
    const b = blockMenu?.block;
    if (!b) return;
    if (b.fullDay) { setWarn(t('dashboard.fullDayNoEdit')); setBlockMenu(null); return; }
    setEditBlockDlg({ id: b.id, date: b.date, start: b.startTime, end: b.endTime, reason: b.reason || '' });
    setBlockMenu(null);
  };
  const submitEditBlock = async () => {
    try {
      await api.updateTimeOff(editBlockDlg.id, {
        startTime: editBlockDlg.start,
        endTime: editBlockDlg.end,
        reason: editBlockDlg.reason || undefined,
      });
      setEditBlockDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.closeTimeError'));
    }
  };
  const removeBlock = async () => {
    try {
      await api.deleteTimeOff(blockMenu.block.id);
      setBlockMenu(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openTimeError'));
    }
  };

  const renderDay = (day, selectedDates, pickersDayProps) => {
    const isToday = day.isSame(today, 'date');
    const isInWeek = day.isSame(startOfWeek, 'week');
    return (
      <PickersDay
        {...pickersDayProps}
        selected={false}
        sx={{
          ...(isToday && {
            bgcolor: '#6c1129',
            color: '#1a1a16',
            '&:hover': { bgcolor: '#977536' },
            fontWeight: 'bold'
          }),
          ...(isInWeek && !isToday && {
            bgcolor: '#6c112922'
          })
        }}
      />
    );
  };

  // --- Перегляд одного дня (діалог) ---
  const dayViewTitle = dayView
    ? `${DAYS[dayjs(dayView).isoWeekday() - 1]}, ${dayjs(dayView).format('DD.MM.YYYY')}`
    : '';
  // bookings уже відфільтровані за майстром (filterMasterId) у reload()
  const dayViewBookings = dayView
    ? bookings
        .filter(b => dayjs(b.date).format('YYYY-MM-DD') === dayView)
        .slice()
        .sort((a, b) => a.time.slice(0, 5).localeCompare(b.time.slice(0, 5)))
    : [];
  const dayViewBlocks = dayView ? timeOff.filter(o => o.date === dayView) : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', px: 0, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center"
            useFlexGap flexWrap="wrap" sx={{ mb: 2, px: 2, gap: 1 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button variant="contained" size="small" onClick={() => setWeekOffset(p => p - 1)}>←</Button>
              <Button variant="contained" size="small" onClick={() => setWeekOffset(p => p + 1)}>→</Button>
              <Button onClick={goToToday}>{t('dashboard.today')}</Button>
              <Button
                size="small"
                variant={showAllHours ? 'contained' : 'outlined'}
                onClick={() => setShowAllHours(v => !v)}
              >
                {showAllHours ? t('dashboard.workHoursOnly') : t('dashboard.allDay')}
              </Button>
              {isManager && members.length > 0 && (
                <TextField
                  select
                  size="small"
                  value={filterMasterId}
                  onChange={(e) => setFilterMasterId(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">{t('dashboard.allMasters')}</MenuItem>
                  {members.map((m) => (
                    <MenuItem key={m.userId} value={String(m.userId)}>{m.name}</MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>
            <Button onClick={handleCalendarOpen}>
              {startOfWeek.format('MMMM YYYY')}
            </Button>
          </Stack>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCalendarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <StaticDatePicker
              value={startOfWeek}
              onChange={handleDateChange}
              displayStaticWrapperAs="desktop"
              renderDay={renderDay}
              sx={{ bgcolor: '#faf7f2', p: 1 }}
              minDate={today}
              shouldDisableDate={date => date.isBefore(today, 'day')}
            />
          </Popover>

          <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: { xs: 620, md: 'auto' } }}>
          <Box display="grid" gridTemplateColumns="60px repeat(7, 1fr)" sx={{ borderBottom: '1px solid rgba(28,29,23,0.18)', px: 2 }}>
            <Box />
            {days.map((d, idx) => (
              <Box
                key={idx}
                onClick={() => setDayView(d.format('YYYY-MM-DD'))}
                sx={{
                  textAlign: 'center',
                  py: 1,
                  fontWeight: 'bold',
                  color: d.isSame(today, 'date') ? '#fff' : '#1a1a16',
                  cursor: 'pointer',
                  bgcolor: d.isSame(today, 'date') ? '#6c1129' : 'transparent',
                  borderLeft: idx !== 0 ? '1px solid rgba(28,29,23,0.12)' : undefined,
                  transition: '0.15s',
                  '&:hover': { bgcolor: d.isSame(today, 'date') ? '#56081f' : 'rgba(108,17,41,0.10)' },
                }}
              >
                {DAYS[idx]}<br />{d.format('DD.MM')}
              </Box>
            ))}
          </Box>

          <Box sx={{ maxHeight: '75vh', overflowY: 'auto', px: 2 }}>
            <Box display="grid" gridTemplateColumns="60px repeat(7, 1fr)">
              {Array.from({ length: slotCount }, (_, i) => {
                const hour = startHour + Math.floor(i / 4);
                const minutes = (i % 4) * 15;
                const timeLabel = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

                return (
                  <React.Fragment key={i}>
                    <Box
                      sx={{
                        textAlign: 'right',
                        pr: 0.75,
                        pt: '2px',
                        height: SLOT_H,
                        boxSizing: 'border-box',
                        fontSize: 12,
                        lineHeight: 1,
                        color: '#5a6660',
                        visibility: minutes === 0 ? 'visible' : 'hidden',
                        borderTop: minutes === 0 ? '1px solid rgba(28,29,23,0.12)' : 'none'
                      }}
                    >
                      {minutes === 0 ? timeLabel : ''}
                    </Box>
                    {days.map((d, dayIdx) => {
                      const dateStr = d.format('YYYY-MM-DD');
                      const slotTime = d.hour(hour).minute(minutes).second(0);
                      const booking = getBookingAt(dateStr, timeLabel);
                      const block = !booking && getBlockAt(dateStr, timeLabel);
                      const isStart = booking && isStartOfBooking(booking, slotTime);
                      const isEnd = booking && isEndOfBooking(booking, slotTime);
                      const isBlockStart = block && (
                        block.fullDay ? (hour === startHour && minutes === 0)
                          : toMin(timeLabel) === toMin(block.startTime)
                      );

                      const outside = !booking && !block && isOutsideWork(d, timeLabel);
                      const background =
                        booking && hoveredId === booking.id
                          ? '#977536'
                          : booking
                          ? 'rgba(201, 162, 74, 0.30)'
                          : block
                          ? 'rgba(28,29,23,0.10)'
                          : outside
                          ? '#eae7df'
                          : '#faf7f2';

                      // «+» на вільних робочих слотах; на позаробочих — прихований, з'являється на hover
                      const showPlus = !booking && !block && (minutes === 0 || minutes === 30);

                      return (
                        <Box
                          key={`${dateStr}-${timeLabel}`}
                          onClick={(e) =>
                            booking
                              ? handleBookingClick(booking.id)
                              : block
                              ? openBlockMenu(e, block)
                              : openSlotMenu(e, dateStr, timeLabel)
                          }
                          onMouseEnter={() => booking && setHoveredId(booking.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          sx={{
                            px: 0.75,
                            py: '2px',
                            height: SLOT_H,
                            boxSizing: 'border-box',
                            position: 'relative',
                            bgcolor: background,
                            color: block ? '#5a6660' : booking ? '#1a1a16' : '#8a887f',
                            borderLeft: dayIdx !== 0 ? '0.5px solid rgba(28,29,23,0.12)' : undefined,
                            borderBottom: booking && isEnd ? '1px solid #6c1129' : 'none',
                            cursor: 'pointer',
                            transition: '0.2s',
                            '&:hover .slot-plus-out': { opacity: 0.4 },
                            borderInlineStart: booking
                              ? '4px solid #6c1129'
                              : block
                              ? '4px solid rgba(28,29,23,0.35)'
                              : undefined,
                            // Пріоритет: початок броні > годинна лінія > півгодинна
                            borderTop:
                              booking && isStart ? '1px solid #6c1129'
                              : minutes === 0 ? '1px solid rgba(28,29,23,0.12)'
                              : minutes === 30 ? '1px dashed rgba(28,29,23,0.12)'
                              : 'none',
                          }}
                        >
                          {booking && isStart ? (
                            // Оверлей на всю висоту броні: не розтягує рядок, зайве обрізається
                            <Box sx={{
                              position: 'absolute',
                              top: 0, left: '4px', right: '2px',
                              height: `${Math.max(1, Math.round((booking.duration || 15) / 15)) * SLOT_H - 2}px`,
                              overflow: 'hidden',
                              zIndex: 2,
                              px: 0.5,
                              display: 'flex',
                              alignItems: 'center', // вміст по центру блоку — без порожньої нижньої половини
                              pointerEvents: 'none',
                            }}>
                              <Stack spacing={0.1} sx={{ width: '100%' }}>
                                <Typography variant="caption" fontWeight="bold" noWrap
                                  sx={{ lineHeight: 1.15, display: 'block' }}>
                                  {booking.checkedOut ? '✓ ' : ''}{booking.userName}
                                </Typography>
                                <Typography variant="caption" noWrap
                                  sx={{ fontSize: '0.7rem', lineHeight: 1.1, display: 'block' }}>
                                  {booking.servicesLabel}
                                </Typography>
                                {isManager && booking.masterName && (
                                  <Typography variant="caption" noWrap
                                    sx={{ fontSize: '0.62rem', lineHeight: 1.1, display: 'block', fontStyle: 'italic', opacity: 0.85 }}>
                                    {booking.masterName}
                                  </Typography>
                                )}
                                <Typography variant="caption" noWrap
                                  sx={{ fontSize: '0.62rem', lineHeight: 1.1, display: 'block',
                                        color: booking.termsAccepted ? 'success.main' : 'warning.dark' }}>
                                  {booking.termsAccepted ? t('dashboard.termsAccepted') : t('dashboard.termsPending')}
                                </Typography>
                                <Typography variant="caption" noWrap
                                  sx={{ fontSize: '0.66rem', lineHeight: 1.1, display: 'block', fontWeight: 700, color: '#5e6a4e' }}>
                                  {formatMoney(effPrice(booking))}
                                </Typography>
                              </Stack>
                            </Box>
                          ) : null}
                          {isBlockStart && (
                            <Typography variant="caption" fontWeight={600}>
                              🔒 {block.fullDay ? t('dashboard.dayOff') : t('dashboard.closed')}
                              {block.reason ? ` · ${block.reason}` : ''}
                            </Typography>
                          )}
                          {showPlus && (
                            <Typography variant="caption"
                              className={outside ? 'slot-plus-out' : undefined}
                              sx={{ opacity: outside ? 0 : 0.35, fontSize: '0.7rem', lineHeight: 1,
                                    display: 'block', transition: 'opacity 0.15s' }}>+</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
          </Box>
          </Box>
        </Box>
      </Box>

      {/* Меню вільного слота */}
      <Menu open={Boolean(slotMenu)} anchorEl={slotMenu?.anchor} onClose={() => setSlotMenu(null)}>
        <MenuItem onClick={createFromSlot}>{t('dashboard.createBooking')}</MenuItem>
        <MenuItem onClick={openCloseDialog}>{t('dashboard.closeTime')}</MenuItem>
        <MenuItem onClick={openOpenHoursDialog}>{t('dashboard.openHours')}</MenuItem>
      </Menu>

      {/* Меню закритого блоку */}
      <Menu open={Boolean(blockMenu)} anchorEl={blockMenu?.anchor} onClose={() => setBlockMenu(null)}>
        {!blockMenu?.block?.fullDay && (
          <MenuItem onClick={openEditBlockDialog}>{t('dashboard.editBlock')}</MenuItem>
        )}
        <MenuItem onClick={removeBlock}>{t('dashboard.openRemoveClosure')}</MenuItem>
      </Menu>

      {/* Діалог закриття годин */}
      <Dialog open={Boolean(closeDlg)} onClose={() => setCloseDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.closeTimeDialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('dashboard.dateLabel')}: {closeDlg?.date}</Typography>
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('dashboard.from')} value={closeDlg?.start || ''}
                onChange={(e) => setCloseDlg(c => ({ ...c, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
              <TextField type="time" label={t('dashboard.to')} value={closeDlg?.end || ''}
                onChange={(e) => setCloseDlg(c => ({ ...c, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            </Stack>
            <TextField label={t('dashboard.reasonOptional')} value={closeDlg?.reason || ''}
              onChange={(e) => setCloseDlg(c => ({ ...c, reason: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloseDlg(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submitClose}>{t('dashboard.closeTime')}</Button>
        </DialogActions>
      </Dialog>

      {/* Діалог «відкрити робочі години» на конкретну дату */}
      <Dialog open={Boolean(openDlg)} onClose={() => setOpenDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.openHoursTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.dateLabel')}: {openDlg?.date}
            </Typography>
            <Typography variant="caption" color="text.secondary">{t('dashboard.openHoursHint')}</Typography>
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('dashboard.from')} value={openDlg?.start || ''}
                onChange={(e) => setOpenDlg(c => ({ ...c, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
              <TextField type="time" label={t('dashboard.to')} value={openDlg?.end || ''}
                onChange={(e) => setOpenDlg(c => ({ ...c, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <span>
            {openDlg?.existingId && (
              <Button color="error" onClick={removeOpenHours}>{t('dashboard.openHoursRemove')}</Button>
            )}
          </span>
          <span>
            <Button onClick={() => setOpenDlg(null)}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={submitOpenHours} sx={{ ml: 1 }}>{t('common.save')}</Button>
          </span>
        </DialogActions>
      </Dialog>

      {/* Діалог редагування закритого блоку */}
      <Dialog open={Boolean(editBlockDlg)} onClose={() => setEditBlockDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.editBlockTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('dashboard.dateLabel')}: {editBlockDlg?.date}</Typography>
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('dashboard.from')} value={editBlockDlg?.start || ''}
                onChange={(e) => setEditBlockDlg(c => ({ ...c, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
              <TextField type="time" label={t('dashboard.to')} value={editBlockDlg?.end || ''}
                onChange={(e) => setEditBlockDlg(c => ({ ...c, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            </Stack>
            <TextField label={t('dashboard.reasonOptional')} value={editBlockDlg?.reason || ''}
              onChange={(e) => setEditBlockDlg(c => ({ ...c, reason: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditBlockDlg(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submitEditBlock}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!warn} onClose={() => setWarn('')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>⚠️ {t('common.attention')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>{warn}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarn('')} variant="contained" autoFocus>OK</Button>
        </DialogActions>
      </Dialog>

      {/* --- Перегляд одного дня: слоти + записи з цінами --- */}
      <Dialog open={!!dayView} onClose={() => setDayView(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
            <span>🗓️ {dayViewTitle}</span>
            <Button size="small" variant="outlined" color="inherit" onClick={() => openHoursForDay(dayView)}>
              {t('dashboard.openHours')}
            </Button>
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}>
            {t('dashboard.dayViewHint')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {dayView && (
            <Box sx={{ position: 'relative', mt: 1 }}>
              {/* Фон: сітка 15-хв слотів; вільні (не закриті) клікабельні для створення */}
              {Array.from({ length: slotCount }, (_, i) => {
                const hour = startHour + Math.floor(i / 4);
                const minutes = (i % 4) * 15;
                const timeLabel = `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                const outside = isOutsideWork(dayjs(dayView), timeLabel);
                const block = getBlockAt(dayView, timeLabel);
                const isHour = minutes === 0;
                const free = !block;
                const rowBorder = isHour ? '1px solid rgba(28,29,23,0.18)' : '1px dashed rgba(28,29,23,0.06)';
                return (
                  <Box key={i}
                    onClick={() => { if (free) { setDayView(null); navigate(`/master/create?date=${dayView}&time=${timeLabel}`); } }}
                    sx={{
                      display: 'grid', gridTemplateColumns: '52px 1fr', height: DAY_SLOT_H,
                      cursor: free ? 'pointer' : 'default',
                      '&:hover .dv-plus': { opacity: free ? 0.5 : 0 },
                    }}>
                    <Box sx={{ pr: 1, textAlign: 'right', fontSize: '0.7rem', color: '#8a887f',
                               borderTop: rowBorder, lineHeight: `${DAY_SLOT_H}px`, boxSizing: 'border-box' }}>
                      {isHour ? timeLabel : ''}
                    </Box>
                    <Box sx={{ position: 'relative', borderTop: rowBorder, boxSizing: 'border-box',
                               bgcolor: block ? 'transparent' : outside ? '#efece6' : '#fbf9f5' }}>
                      <Typography className="dv-plus" sx={{
                        position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                        opacity: 0, transition: '0.15s', fontSize: '0.9rem', color: '#6c1129', fontWeight: 700 }}>＋</Typography>
                    </Box>
                  </Box>
                );
              })}

              {/* Закриті блоки / вихідні */}
              {dayViewBlocks.map((o) => {
                const rangeStartMin = startHour * 60;
                const rangeEndMin = startHour * 60 + slotCount * 15;
                const bStart = o.fullDay ? rangeStartMin : toMin(o.startTime);
                const bEnd = o.fullDay ? rangeEndMin : toMin(o.endTime);
                const clStart = Math.max(bStart, rangeStartMin);
                const clEnd = Math.min(bEnd, rangeEndMin);
                if (clEnd <= clStart) return null;
                const top = ((clStart - rangeStartMin) / 15) * DAY_SLOT_H;
                const height = ((clEnd - clStart) / 15) * DAY_SLOT_H;
                return (
                  <Box key={`blk-${o.id}`} sx={{
                    position: 'absolute', left: '52px', right: 0, top, height,
                    bgcolor: 'rgba(28,29,23,0.10)', borderInlineStart: '4px solid rgba(28,29,23,0.35)',
                    display: 'flex', alignItems: 'center', px: 1, boxSizing: 'border-box', overflow: 'hidden' }}>
                    <Typography variant="caption" noWrap sx={{ color: '#5a6660' }}>
                      🔒 {o.fullDay ? t('dashboard.dayOff') : t('dashboard.closed')}{o.reason ? ` · ${o.reason}` : ''}
                    </Typography>
                  </Box>
                );
              })}

              {/* Записи — клік → деталі */}
              {dayViewBookings.map((b) => {
                const start = b.time.slice(0, 5);
                const top = ((toMin(start) - startHour * 60) / 15) * DAY_SLOT_H;
                const height = Math.max(1, Number(b.duration || 0) / 15) * DAY_SLOT_H;
                const end = dayjs(`2000-01-01T${start}`).add(Number(b.duration || 0), 'minute').format('HH:mm');
                return (
                  <Box key={b.id} onClick={() => { setDayView(null); handleBookingClick(b.id); }}
                    sx={{
                      position: 'absolute', left: '52px', right: 0, top, height, overflow: 'hidden',
                      bgcolor: '#f3e4e9', borderInlineStart: '4px solid #6c1129', border: '1px solid #dcb9c4',
                      borderRadius: '2px', px: 1, py: '2px', cursor: 'pointer', boxSizing: 'border-box',
                      transition: '0.15s', '&:hover': { bgcolor: '#dad6ff' } }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                        {b.checkedOut ? '✓ ' : ''}{start}–{end}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5e6a4e' }} noWrap>{formatMoney(effPrice(b))}</Typography>
                    </Stack>
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600, lineHeight: 1.15 }}>
                      {b.userName || b.clientName}
                    </Typography>
                    {isManager && b.masterName && height >= DAY_SLOT_H * 2 && (
                      <Typography variant="caption" noWrap sx={{ display: 'block', color: '#5a6660', fontStyle: 'italic' }}>
                        {b.masterName}
                      </Typography>
                    )}
                    {b.servicesLabel && height >= DAY_SLOT_H * 3 && (
                      <Typography variant="caption" noWrap sx={{ display: 'block', color: '#5a6660' }}>
                        {b.servicesLabel}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
          {dayView && dayViewBookings.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('dashboard.dayNoBookings')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDayView(null)} variant="outlined" color="inherit">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
